import { Outlet } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useOnboardStore } from '@/shared/stores/onboardStore';
import { ONBOARDING_STEPS } from '@/features/onboarding/types/onboarding.types';
import { cn } from '@/shared/lib/utils';

/**
 * OnboardingLayout — progress bar + form layout for new organization setup.
 */
export function OnboardingLayout() {
  const { t } = useTranslation('onboarding');
  const { t: tCommon } = useTranslation();
  const { currentStep, completedSteps, totalSteps } = useOnboardStore();

  const progressPercent = Math.round(((completedSteps.length) / totalSteps) * 100);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Top bar */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-border px-6">
        <span className="text-lg font-semibold text-foreground">
          {tCommon('app.name')}
        </span>
        <span className="text-xs text-muted-foreground">
          {t('progress', { current: currentStep + 1, total: totalSteps })}
        </span>
      </header>

      {/* Progress bar */}
      <div className="w-full bg-muted">
        <div
          className="h-1 bg-primary transition-all duration-300"
          style={{ width: `${String(progressPercent)}%` }}
        />
      </div>

      {/* Step indicators (compact dots) */}
      <div className="mx-auto flex w-full max-w-3xl items-center justify-center gap-1 px-4 pt-6">
        {ONBOARDING_STEPS.map((stepKey, index) => (
          <div
            key={stepKey}
            className={cn(
              'h-1.5 flex-1 rounded-full transition-colors',
              index < currentStep && 'bg-primary',
              index === currentStep && 'bg-primary',
              index > currentStep && 'bg-muted',
              completedSteps.includes(index) && 'bg-primary',
            )}
            title={t(`steps.${stepKey}`)}
          />
        ))}
      </div>

      {/* Form content */}
      <div className="mx-auto w-full max-w-2xl px-4 py-8">
        <div className="rounded-lg border border-transparent bg-card p-6 shadow-card dark:border-border">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
